import React from "react";
import Navbar from "@/components/stori/Navbar";
import Footer from "@/components/stori/Footer";
import VideoGallery from "@/components/stori/VideoGallery";
import Capabilities from "@/components/stori/Capabilities";
import TestimonialWall from "@/components/stori/TestimonialWall";
import { LeadModalProvider } from "@/components/stori/LeadModalContext";
import LeadFormModal from "@/components/stori/LeadFormModal";
import { useLeadModal } from "@/components/stori/LeadModalContext";
import {
  ArrowRight,
  Database,
  Crown,
  Workflow,
  RefreshCcw,
  Hotel,
  Utensils,
  ShoppingBag,
  Sparkles,
} from "lucide-react";
import SectionHeading from "@/components/stori/SectionHeading";
import Seo from "@/seo/Seo";

// --------------------------------------------------------------------------
// Hero
// --------------------------------------------------------------------------
function MarketersHero() {
  const { openModal } = useLeadModal();
  return (
    <section
      data-testid="marketers-hero"
      className="relative w-full min-h-[80svh] overflow-hidden flex flex-col items-start justify-center pt-32 pb-24 border-b border-stori-border"
    >
      <div className="absolute inset-0 z-0 pointer-events-none">
        <img
          src="https://images.unsplash.com/photo-1552664730-d307ca884978?auto=format&fit=crop&w=2400&q=80"
          alt=""
          aria-hidden="true"
          className="absolute inset-0 w-full h-full object-cover"
          style={{ objectPosition: "center 40%", filter: "saturate(0.95) contrast(1.05)" }}
        />
        <div className="absolute inset-0 bg-gradient-to-r from-stori-bg via-stori-bg/85 to-stori-bg/30" />
        <div className="absolute inset-y-0 left-0 w-[40%] bg-stori-bg" />
        <div className="absolute inset-y-0 left-[40%] w-[18%] bg-gradient-to-r from-stori-bg to-transparent" />
        <div className="absolute inset-x-0 bottom-0 h-40 bg-gradient-to-t from-stori-bg to-transparent" />
        <div className="absolute -top-20 -left-20 w-[600px] h-[600px] bg-stori-cyan/12 rounded-full blur-[140px]" />
      </div>

      <div className="relative z-10 max-w-[1400px] w-full mx-auto px-6 lg:px-10">
        <div className="max-w-3xl">
          <div className="label-eyebrow text-stori-cyan flex items-center gap-2 mb-6">
            <span className="inline-block h-px w-10 bg-stori-cyan" />
            FOR MARKETING TEAMS
          </div>
          <h1
            data-testid="marketers-headline"
            className="font-heading text-[clamp(2.5rem,6vw,5rem)] leading-[1.04] font-semibold tracking-tight text-white"
          >
            <span className="block fade-up">Turn guest moments into</span>
            <span className="block fade-up text-stori-cyan cyan-text-glow" style={{ animationDelay: "120ms" }}>
              automated outcomes.
            </span>
          </h1>
          <p className="mt-7 text-lg lg:text-xl text-stori-muted max-w-2xl leading-relaxed">
            Built for marketing teams running luxury hotels, fine dining, retail flagships,
            wellness, and cultural spaces. Capture clean first-party data, identify
            high-value guests, and trigger personalized playbooks — automatically.
          </p>
          <div className="mt-10 flex flex-wrap items-center gap-3">
            <button
              type="button"
              onClick={() => openModal("demo_request")}
              data-testid="marketers-cta-demo"
              className="inline-flex items-center gap-2 bg-stori-cyan text-black font-medium px-6 py-3.5 rounded-md hover:bg-stori-cyanHover transition-all hover:translate-y-[-1px]"
            >
              Book a demo
              <ArrowRight className="w-4 h-4" />
            </button>
            <button
              type="button"
              onClick={() => openModal("trial_signup")}
              data-testid="marketers-cta-trial"
              className="inline-flex items-center gap-2 border border-stori-cyan/60 text-stori-cyan px-6 py-3.5 rounded-md hover:bg-stori-cyan/10 transition-colors"
            >
              Get started for free
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}

// --------------------------------------------------------------------------
// Value props
// --------------------------------------------------------------------------
const VALUE_PROPS = [
  {
    icon: Database,
    title: "Clean first-party data",
    desc: "Every guest interaction generates compliant, segmented data — yours to keep, no third-party loss.",
    stat: "87%",
    statLabel: "avg. email capture",
  },
  {
    icon: Crown,
    title: "Influencer + VIP detection",
    desc: "AI flags high-reach guests in real-time so your team treats them differently — without manual work.",
    stat: "28%",
    statLabel: "of guests flagged as high-value",
  },
  {
    icon: Workflow,
    title: "Playbooks, on autopilot",
    desc: "Pre-built workflows trigger by guest type — VIP, family, business, standard — each with its own sequence.",
    stat: "3.2x",
    statLabel: "higher follow-up conversion",
  },
  {
    icon: RefreshCcw,
    title: "Native CRM sync",
    desc: "Real-time sync to Salesforce, HubSpot, Klaviyo, and Mailchimp. No manual exports, no orphaned records.",
    stat: "0",
    statLabel: "manual data entry",
  },
];

function ValueProps() {
  return (
    <section
      data-testid="marketers-value-props"
      className="relative py-24 lg:py-32 border-b border-stori-border"
    >
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
        <SectionHeading
          eyebrow="WHAT YOUR TEAM GETS"
          title="Marketing infrastructure that runs your space."
          lead="Stori turns a passive guest experience into a continuous loop of capture, classify, trigger, measure."
        />
        <div className="mt-16 grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {VALUE_PROPS.map((v, i) => {
            const Icon = v.icon;
            return (
              <article
                key={v.title}
                data-testid={`marketers-value-${i}`}
                className="p-7 rounded-2xl border border-stori-border bg-stori-surface1 hover:border-stori-cyan/40 transition-colors flex flex-col"
              >
                <div className="w-11 h-11 rounded-lg border border-stori-border text-stori-cyan flex items-center justify-center mb-6">
                  <Icon className="w-5 h-5" />
                </div>
                <h3 className="font-heading text-xl font-semibold text-white">{v.title}</h3>
                <p className="mt-2 text-sm text-stori-muted leading-relaxed flex-1">{v.desc}</p>
                <div className="mt-6 pt-5 border-t border-stori-border flex items-baseline gap-3">
                  <span className="font-heading text-3xl font-semibold text-stori-cyan cyan-text-glow">
                    {v.stat}
                  </span>
                  <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-stori-muted leading-tight">
                    {v.statLabel}
                  </span>
                </div>
              </article>
            );
          })}
        </div>
      </div>
    </section>
  );
}

// --------------------------------------------------------------------------
// Scenarios — three side-by-side example installations
// --------------------------------------------------------------------------
const SCENARIOS = [
  {
    icon: Hotel,
    image: "https://images.unsplash.com/photo-1582719478250-c89cae4dc85b?auto=format&fit=crop&w=1200&q=80",
    venue: "Boutique Hotel · Lobby",
    headline: "From anonymous stays to engineered loyalty.",
    bullets: [
      "Lobby installation captures 80%+ of arrivals",
      "VIP playbook triggers welcome amenity for influencers",
      "34% of engaged guests rebook within 90 days",
    ],
  },
  {
    icon: Utensils,
    image: "https://images.unsplash.com/photo-1414235077428-338989a2e8c0?auto=format&fit=crop&w=1200&q=80",
    venue: "Premium Restaurant · Entrance",
    headline: "From one-night dining to repeat reservations.",
    bullets: [
      "Captures dietary + occasion preferences at entry",
      "Loyalty playbook fires within 24h of dinner",
      "18% → 42% repeat-visit rate in 6 months",
    ],
  },
  {
    icon: ShoppingBag,
    image: "https://images.unsplash.com/photo-1441986300917-64674bd600d8?auto=format&fit=crop&w=1200&q=80",
    venue: "Retail Flagship · In-store",
    headline: "From foot traffic to product intelligence.",
    bullets: [
      "Captures style + size preferences via on-brand AI photos",
      "Generates 200+ social posts/month for free reach",
      "Feeds preference data directly to merchandising team",
    ],
  },
];

function Scenarios() {
  return (
    <section
      data-testid="marketers-scenarios"
      className="relative py-24 lg:py-32 border-b border-stori-border bg-stori-bg"
    >
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
        <SectionHeading
          eyebrow="HOW IT LOOKS IN YOUR VENUE"
          title="Three rooms. Three playbooks. One platform."
        />
        <div className="mt-16 grid md:grid-cols-3 gap-5">
          {SCENARIOS.map((s, i) => {
            const Icon = s.icon;
            return (
              <article
                key={i}
                data-testid={`marketers-scenario-${i}`}
                className="rounded-2xl overflow-hidden border border-stori-border bg-stori-surface1 hover:border-stori-cyan/40 transition-colors group"
              >
                <div className="relative aspect-[4/3] overflow-hidden">
                  <img
                    src={s.image}
                    alt={s.venue}
                    loading="lazy"
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-stori-bg via-stori-bg/30 to-transparent" />
                  <div className="absolute bottom-3 left-4 flex items-center gap-2">
                    <Icon className="w-4 h-4 text-stori-cyan" />
                    <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-white">
                      {s.venue}
                    </span>
                  </div>
                </div>
                <div className="p-7">
                  <h3 className="font-heading text-2xl font-semibold text-white tracking-tight leading-tight">
                    {s.headline}
                  </h3>
                  <ul className="mt-5 space-y-2.5">
                    {s.bullets.map((b, j) => (
                      <li key={j} className="flex items-start gap-2 text-sm text-stori-muted leading-relaxed">
                        <span className="mt-2 h-1 w-1 rounded-full bg-stori-cyan flex-shrink-0" />
                        <span>{b}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </article>
            );
          })}
        </div>
      </div>
    </section>
  );
}

// --------------------------------------------------------------------------
// Featured case study
// --------------------------------------------------------------------------
function FeaturedCase() {
  const { openModal } = useLeadModal();
  return (
    <section
      data-testid="marketers-featured-case"
      className="relative py-24 lg:py-32 border-b border-stori-border"
    >
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10 grid lg:grid-cols-2 gap-12 items-center">
        <div className="rounded-2xl overflow-hidden border border-stori-border">
          <img
            src="https://images.unsplash.com/photo-1566073771259-6a8506099945?auto=format&fit=crop&w=1400&q=80"
            alt="Aurelia Hotel Group lobby"
            loading="lazy"
            className="w-full h-full object-cover aspect-[4/3]"
          />
        </div>
        <div>
          <div className="label-eyebrow text-stori-cyan flex items-center gap-2 mb-5">
            <span className="inline-block h-px w-8 bg-stori-cyan" />
            CASE STUDY · HOSPITALITY
          </div>
          <h2 className="font-heading text-4xl lg:text-5xl font-semibold text-white tracking-tight leading-[1.1]">
            Aurelia Hotel Group turned the lobby into a loyalty engine.
          </h2>
          <div className="mt-10 grid grid-cols-3 gap-4">
            <div>
              <div className="font-heading text-3xl font-semibold text-stori-cyan cyan-text-glow">680</div>
              <div className="font-mono text-[10px] uppercase tracking-[0.18em] text-stori-muted mt-1">
                emails captured · month 1
              </div>
            </div>
            <div>
              <div className="font-heading text-3xl font-semibold text-stori-cyan cyan-text-glow">34%</div>
              <div className="font-mono text-[10px] uppercase tracking-[0.18em] text-stori-muted mt-1">
                rebook rate · 90d
              </div>
            </div>
            <div>
              <div className="font-heading text-3xl font-semibold text-stori-cyan cyan-text-glow">$180K</div>
              <div className="font-mono text-[10px] uppercase tracking-[0.18em] text-stori-muted mt-1">
                attributed revenue · 6mo
              </div>
            </div>
          </div>
          <blockquote className="mt-8 text-xl text-white leading-snug font-medium">
            &ldquo;The space became a lead-generation machine. What used to be decoration is now driving loyalty.&rdquo;
          </blockquote>
          <div className="mt-3 font-mono text-[11px] uppercase tracking-[0.22em] text-stori-muted">
            — Daniel Hahn · GM, Aurelia Singapore
          </div>
          <button
            type="button"
            onClick={() => openModal("demo_request")}
            data-testid="marketers-case-cta"
            className="mt-10 inline-flex items-center gap-2 bg-stori-cyan text-black font-medium px-6 py-3.5 rounded-md hover:bg-stori-cyanHover transition-colors"
          >
            See how this works for your venue
            <ArrowRight className="w-4 h-4" />
          </button>
        </div>
      </div>
    </section>
  );
}

// --------------------------------------------------------------------------
// CTA banner
// --------------------------------------------------------------------------
function CTABanner() {
  const { openModal } = useLeadModal();
  return (
    <section
      data-testid="marketers-cta"
      className="relative py-28 lg:py-40 border-b border-stori-border overflow-hidden"
    >
      <div className="absolute inset-0 grid-noise opacity-40 radial-mask" />
      <div className="absolute -top-40 left-1/2 -translate-x-1/2 w-[800px] h-[800px] bg-stori-cyan/15 rounded-full blur-[120px]" />
      <div className="relative max-w-[1100px] mx-auto px-6 lg:px-10 text-center">
        <div className="label-eyebrow text-stori-cyan flex items-center justify-center gap-2 mb-6">
          <span className="inline-block h-px w-10 bg-stori-cyan" />
          GET STARTED
          <span className="inline-block h-px w-10 bg-stori-cyan" />
        </div>
        <h2 className="font-heading text-5xl sm:text-6xl lg:text-7xl font-semibold text-white tracking-tight leading-[1.04]">
          See the dashboard your team
          <br />
          <span className="text-stori-cyan cyan-text-glow">will actually use.</span>
        </h2>
        <p className="mt-8 text-lg text-stori-muted max-w-2xl mx-auto leading-relaxed">
          A 20-minute demo with the same playbook builder, segmentation engine, and ROI
          calculator that's already live across 27 countries of luxury operators.
        </p>
        <div className="mt-12 flex flex-wrap items-center justify-center gap-3">
          <button
            type="button"
            onClick={() => openModal("demo_request")}
            data-testid="marketers-cta-banner-demo"
            className="inline-flex items-center gap-2 bg-stori-cyan text-black font-semibold px-7 py-4 rounded-md hover:bg-stori-cyanHover transition-all hover:translate-y-[-1px]"
          >
            Book a demo
            <ArrowRight className="w-4 h-4" />
          </button>
          <button
            type="button"
            onClick={() => openModal("trial_signup")}
            data-testid="marketers-cta-banner-trial"
            className="inline-flex items-center gap-2 border border-stori-cyan/60 text-stori-cyan px-7 py-4 rounded-md hover:bg-stori-cyan/10 transition-colors font-medium"
          >
            <Sparkles className="w-4 h-4" />
            Get started for free
          </button>
        </div>
      </div>
    </section>
  );
}

// --------------------------------------------------------------------------
// Page export
// --------------------------------------------------------------------------
export default function Marketers() {
  return (
    <LeadModalProvider>
      <Seo path="/marketers" />
      <main data-testid="marketers-page" className="bg-stori-bg text-white overflow-x-hidden">
        <Navbar />
        <MarketersHero />
        <ValueProps />
        <Capabilities />
        <Scenarios />
        <VideoGallery />
        <FeaturedCase />
        <TestimonialWall />
        <CTABanner />
        <Footer />
        <LeadFormModal />
      </main>
    </LeadModalProvider>
  );
}
